/**
 * MeetingContextPanel - the Docs tab's top card: the AI-maintained meeting context in two blocks,
 * "Summary & notes" (last-meeting recap) and "Follow-ups" (next-meeting prep). Every item carries
 * clickable source-doc chips (open the doc in the existing DealDocumentViewPanel sub-view); an
 * update after a new doc is processed puts that doc's chip first. Regenerates automatically after
 * each processed doc (the header shows "Regenerating…" while the server works) and manually via
 * the Regenerate button (hidden for viewers).
 */

import type { MeetingContextItem, SalesDealMeetingContext } from '@protoss/shared';
import { Button, FileIcon, ReloadIcon, SkeletonBlock, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { formatRelativeTime } from '../../../lib/datetime';

const FOLLOW_UPS_CAP = 8;

function SourceChips({
  refs,
  onOpenDocument,
}: {
  refs: MeetingContextItem['sourceRefs'];
  onOpenDocument: (docUuid: string) => void;
}): React.ReactElement | null {
  // Dedupe by document - an item may cite the same doc through several refs.
  const unique = refs.filter((r, i, a) => a.findIndex((x) => x.documentUuid === r.documentUuid) === i);
  if (unique.length === 0) return null;
  return (
    <span className="deal-meeting-context__item-sources">
      {unique.map((r) => (
        <button
          key={r.documentUuid}
          type="button"
          className="deal-meeting-context__source-chip"
          title={r.documentTitle ?? 'Source document'}
          onClick={() => onOpenDocument(r.documentUuid)}
        >
          <FileIcon width={12} height={12} aria-hidden="true" />
          <span className="deal-meeting-context__source-chip-label">
            {r.documentTitle ?? 'Source'}
          </span>
        </button>
      ))}
    </span>
  );
}

function ContextBlock({
  title,
  items,
  capped,
  onOpenDocument,
}: {
  title: string;
  items: MeetingContextItem[];
  capped?: boolean;
  onOpenDocument: (docUuid: string) => void;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const visible = capped && !expanded ? items.slice(0, FOLLOW_UPS_CAP) : items;
  return (
    <div className="deal-meeting-context__block">
      <div className="deal-meeting-context__block-title">{title}</div>
      {items.length === 0 ? (
        <p className="deal-meeting-context__block-empty">Nothing yet.</p>
      ) : (
        <ul className="deal-meeting-context__list">
          {visible.map((item, i) => (
            <li key={i} className="deal-meeting-context__item">
              <p className="deal-meeting-context__item-text">{item.text}</p>
              <SourceChips refs={item.sourceRefs} onOpenDocument={onOpenDocument} />
            </li>
          ))}
        </ul>
      )}
      {capped && items.length > FOLLOW_UPS_CAP ? (
        <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : `Show all ${items.length}`}
        </Button>
      ) : null}
    </div>
  );
}

export interface MeetingContextPanelProps {
  context: SalesDealMeetingContext | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;
  /** False for viewers - the panel renders read-only without the Regenerate button. */
  canRegenerate: boolean;
  onRegenerate: () => void;
  onOpenDocument: (docUuid: string) => void;
}

export function MeetingContextPanel({
  context,
  isLoading,
  isGenerating,
  error,
  canRegenerate,
  onRegenerate,
  onOpenDocument,
}: MeetingContextPanelProps): React.ReactElement {
  const payload = context?.status === 'generated' ? context.payload : null;

  return (
    <section className="deal-meeting-context" aria-label="Meeting context">
      <div className="deal-meeting-context__header">
        <span className="deal-meeting-context__title">Meeting context</span>
        {payload && context?.generatedAt ? (
          <Tooltip.Simple label={new Date(context.generatedAt).toLocaleString()}>
            <span className="deal-meeting-context__meta">
              Updated {formatRelativeTime(context.generatedAt)}
            </span>
          </Tooltip.Simple>
        ) : null}
        {isGenerating ? (
          <span className="deal-meeting-context__generating">
            <ReloadIcon
              width={12}
              height={12}
              className="deal-meeting-context__spinner"
              aria-hidden="true"
            />
            {payload ? 'Regenerating…' : 'Generating…'}
          </span>
        ) : null}
        {canRegenerate ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="deal-meeting-context__regenerate"
            disabled={isGenerating}
            onClick={onRegenerate}
          >
            {payload ? 'Regenerate' : 'Generate'}
          </Button>
        ) : null}
      </div>
      {error ? <p className="deal-meeting-context__error">{error}</p> : null}
      {isLoading || (isGenerating && !payload) ? (
        <div className="deal-meeting-context__skeletons">
          <SkeletonBlock className="deal-meeting-context__skeleton" />
          <SkeletonBlock className="deal-meeting-context__skeleton" />
        </div>
      ) : payload ? (
        <div className="deal-meeting-context__blocks">
          <ContextBlock
            title="Summary & notes"
            items={payload.summaryNotes}
            onOpenDocument={onOpenDocument}
          />
          <ContextBlock
            title="Follow-ups"
            items={payload.followUps}
            capped
            onOpenDocument={onOpenDocument}
          />
        </div>
      ) : (
        <p className="deal-meeting-context__empty">
          No meeting context yet - it builds automatically when a meeting document is processed
          {canRegenerate ? ', or generate it now from the documents already attached' : ''}.
        </p>
      )}
    </section>
  );
}
